import { Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { SubStepEnum } from '../models/sub-step.enum';
import { getMaxHeight } from '../../../../../services/helpers/fence-helper';
import { TranslateService } from '@ngx-translate/core';
import { MaterialsFacadeService } from '../services/materials.facade.service';
import { FenceService } from '../../../../../services/configurators-2d/applications/fence.service';
import { MaterialSubStepService } from '../services/material-sub-step.service';
import { Configuration } from '../../../../../models/configurators-2d/applications/fence/configuration';
import { Decoration } from '../../../../../models/configurators-2d/applications/fence/decoration';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { DecorationStateService } from '../../services/decoration.state.service';
import { InstallationTypeEnum } from 'src/app/enum/fence/installation-type.enum';

@Component({
  selector: 'ng-casto-materials-panels',
  templateUrl: './materials-panels.component.html',
  styleUrl: './materials-panels.component.scss',
})
export class MaterialsPanelsComponent implements OnInit {
  /** Langue sélectionnée */
  currentLang: string;

  /** Base URL de l'API */
  baseImgURL: string = undefined;
  protected readonly SubStepEnum = SubStepEnum;
  protected readonly InstallationTypeEnum = InstallationTypeEnum;
  protected readonly getMaxHeight = getMaxHeight;
  private readonly translateService = inject(TranslateService);
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fenceService = inject(FenceService);
  private readonly materialSubStepService = inject(MaterialSubStepService);
  private readonly decorationStateService = inject(DecorationStateService);

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  /**
   * Retourne le message de déréférencement
   */
  protected get disableMessage(): string {
    return this.materialsFacadeService.disableMessage();
  }

  /**
   * Retourne la configuration actuelle
   */
  protected get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  /**
   * Retourne la sous-étape courante du composant
   * @protected
   */
  protected get subStep(): SubStepEnum {
    return this.materialSubStepService.subStep();
  }

  /**
   * Retourne la liste des panneaux disponibles
   */
  protected get panels(): Decoration[] {
    return this.decorationStateService.panels();
  }

  /**
   * Vérifie si la liste de décorations contient des éléments désactivés
   * @returns true si au moins une décoration est désactivée, false sinon
   */
  protected get isPanelsValid(): boolean {
    return this.materialsFacadeService.isPanelsValid();
  }

  /**
   * Est-ce que la liste des panneaux contient des éléments pouvant être placés horizontalement ?
   */
  hasHorizontalPanels = computed(() => {
    return this.panels.some(x => x.canBeHorizontal && !this.configuration.isVertical);
  });

  /**
   * Est-ce que la liste des panneaux contient des éléments pouvant être placés verticalement ?
   */
  hasVerticalPanels = computed(() => {
    return this.panels.some(x => x.canBeVertical && getMaxHeight(this.configuration) >= 183);
  });

  /**
   * Est-ce que la liste des panneaux contient des éléments pouvant être placés horizontalement ou verticalement ?
   */
  hasPanels = computed(() => {
    return this.hasHorizontalPanels() || this.hasVerticalPanels();
  });

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Gère l'affichage/masquage des sous-menus en accordéon
   * @param step - Numéro du sous-menu (1: Lames, 2: Poteaux, 3: Lames décoratives, 4: Décors, 5: Panneaux, 6: Portillons)
   */
  protected toggleCollapse(step: SubStepEnum): void {
    this.materialSubStepService.toggleCollapse(step);
  }

  /**
   * Supprime tous les panneaux de la configuration
   */
  protected deleteAllPanels(): void {
    this.materialsFacadeService
      .deleteAllPanels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur suppression de tout les panels:', err),
      });
  }

  /**
   * Ajoute ou retire un panneau de la configuration
   * @param type - Type de panneau ('horizontal' ou 'vertical')
   * @param panel - Le panneau à ajouter/retirer
   */
  protected addPanel(type: string, panel: Decoration): void {
    this.materialsFacadeService
      .addPanel(type, panel)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur ajout/suppression panel:', err),
      });
  }

  /**
   * Vérifie si un panneau est coché (sélectionné) dans la configuration
   * @param type - Type de panneau ('horizontal' ou 'vertical')
   * @param panel - Le panneau à vérifier
   * @returns true si le panneau est coché, false sinon
   */
  protected isPanelChecked(type: 'horizontal' | 'vertical', panel: Decoration): boolean {
    return this.materialsFacadeService.isPanelChecked(type, panel.id);
  }

  /**
   * Modifie la quantité d'un panneau
   * @param type - Type de panneau ('horizontal' ou 'vertical')
   * @param panel - Le panneau concerné
   * @param sideId - Identifiant du côté (pour les panneaux verticaux), null pour les horizontaux
   * @param event - L'événement de changement
   */
  protected changePanelQuantity(
    type: 'horizontal' | 'vertical',
    panel: Decoration,
    sideId: string | null,
    event: Event
  ): void {
    this.materialsFacadeService
      .changePanelQuantity(type, panel, sideId, event)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement quantité panel:', err),
      });
  }

  /**
   * Calcule le nombre maximum d'éléments horizontaux (lames décoratives/décorations/panneaux) autorisés
   * Prend en compte le nombre de panneaux complets et les éléments déjà placés
   * @param decoration - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @returns Le nombre maximum d'éléments horizontaux autorisés
   */
  protected maxHorizontalElement(decoration: Decoration): number {
    return this.materialsFacadeService.maxHorizontalElement(decoration);
  }

  /**
   * Récupère la quantité d'un panneau
   * @param type - Type de panneau ('horizontal' ou 'vertical')
   * @param panelId - Identifiant du panneau
   * @param sideId - Identifiant du côté (pour les panneaux verticaux), null pour les horizontaux
   * @returns La quantité du panneau
   */
  protected getPanelQuantity(type: 'horizontal' | 'vertical', panelId: number, sideId: string | null): number {
    return this.materialsFacadeService.getPanelQuantity(type, panelId, sideId);
  }

  /**
   * Calcule le nombre maximum d'éléments verticaux (lames décoratives/décorations/panneaux) autorisés pour un côté
   * Prend en compte la longueur du côté et les éléments déjà placés
   * @param decoration - Élément (lame décorative ou décoration ou panneau) pour lequel calculer le maximum
   * @param side - Le côté de la clôture
   * @returns Le nombre maximum d'éléments verticaux autorisés pour ce côté
   */
  protected maxVerticalElement(decoration: Decoration, side: any): number {
    return this.materialsFacadeService.maxVerticalElement(decoration, side);
  }
}
